import { Link } from "react-router-dom";
import SeriesNavigation from "../../components/SeriesNavigation";
import { media } from "../../data/navigationData";
import SEO from "../../components/SEO";
import ScrollDepthTracker from "../../components/ScrollDepthTracker";
import { trackEvent } from "../../utils/analytics";

export default function ABCNews() {
  const abcNewsSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "ABC News: Red Blood Cell Roadblocks",
    "url": "https://www.thrombio.com.au/media/abc",
    "mainEntity": {
      "@type": "Organization",
      "name": "Thrombio",
      "url": "https://www.thrombio.com.au/",
      "logo": "https://www.thrombio.com.au/thromBioInvert.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "info@thrombio.com.au"
      },
      "sameAs": [
        "https://www.linkedin.com/company/thrombioholdings"
      ]
    },
    "about": {
      "@type": "MedicalEntity",
      "name": "TBO-309 Stroke Research",
      "description": "ABC News coverage of ThromBio's breakthrough research on red blood cell roadblocks and TBO-309 anti-clotting drug for stroke treatment."
    }
  };

  return (
    <>
      <SEO
        title="ABC News: Red Blood Cell Roadblocks"
        description="ABC News covers ThromBio's breakthrough research revealing how red blood cells create roadblocks in stroke patients and the development of TBO-309 anti-clotting drug."
        keywords={['ABC News', 'Red Blood Cell Roadblocks', 'TBO-309', 'Stroke Research', 'Prof Shaun Jackson', 'Microcirculation', 'Microclots', 'Red cell aggregates', 'Stroke', 'TBO-309', 'Stroke recovery', 'Blood vessel blockage', 'Anti-clotting drug', 'Stroke treatment', 'Novel therapeutics', 'Anti-platelet', 'Clinical trial', 'Platelet inhibitor']}
        ogImage="/video-poster.png"
        schema={abcNewsSchema}
      />
      <ScrollDepthTracker pageCategory="media" pageName="abc" />

      <div className="bg-primary">
        <div className="page-width px-6 md:px-20 pt-[18px] pb-24">
          <nav className="flex items-center gap-2 font-sans text-[2rem] font-normal leading-[2.3rem]" aria-label="Breadcrumb">
            <Link to="/media" className="text-white hover:text-primary-red transition-colors">
              Media
            </Link>
            <span className="mx-1 text-white">/</span>
            <span className="text-[#5C95A4]">ABC News: Stroke Drug</span>
          </nav>
        </div>

        <div className="page-width px-6 md:px-20 pb-16 text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 mb-[120px] lg:items-end">
            <div className="flex flex-col items-center lg:items-start lg:flex-shrink-0 lg:justify-end">
              <img src="/abcNewsLogo.webp" alt="ABC News Logo" className="h-[80px] md:h-[100px] w-auto" />
            </div>
            
            <div className="flex-1 max-w-[800px]">
                <div className="w-full bg-gray-200 rounded-lg overflow-hidden relative group">
                {/* Custom Play Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none group-hover:opacity-0 transition-opacity duration-300 z-10">
                    <div className="bg-black/60 rounded-full p-6 backdrop-blur-sm">
                    <svg 
                        className="w-16 h-16 text-white" 
                        fill="currentColor" 
                        viewBox="0 0 24 24"
                    >
                        <path d="M8 5v14l11-7z"/>
                    </svg>
                    </div>
                </div>
                
                <video
                    controls
                    poster="/video-poster.webp"
                    onPlay={() => trackEvent('video_play', { video_name: 'abc-thrombio', page_path: '/media/abc' })}
                    className="w-full h-auto [&::-webkit-media-controls]:opacity-0 [&::-webkit-media-controls]:transition-opacity [&::-webkit-media-controls]:duration-300 hover:[&::-webkit-media-controls]:opacity-100"
                >
                    <source src="/abc-thrombio-video.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                </div>
            </div>
          </div>

          <div className="flex flex-col gap-8 mb-[30px]">
            <div className="text-white font-sans text-[4.4rem] font-bold leading-[5.8rem]">
              Red Blood Cell Roadblocks: <span className='font-light'>Why the Smallest Vessels Might Hold the Biggest Key to Stroke Recovery</span>
            </div>
          </div>

          <div className="flex flex-col gap-8 mb-[30px]">
            <div className="text-[#9EE7FF] font-syne text-[2.8rem] font-bold leading-[3.8rem] tracking-[.14rem]">
              The Part of Stroke We've Been Missing
            </div>
            <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
              Most people think a stroke is all about a big blood clot in a big artery. And they're not wrong—that's where the emergency starts. But in many cases, even after doctors clear that big clot, brain tissue keeps dying.
            </div>
            <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
              Why? Because the smallest blood vessels—the brain's microcirculation—stay blocked. This can lead to the dreaded "no-reflow phenomenon", and it's one of the biggest unsolved problems in stroke care.
            </div>
          </div>

          <div className="flex flex-col gap-8 mb-[30px]">
            <div className="text-[#9EE7FF] font-syne text-[2.8rem] font-bold leading-[3.8rem] tracking-[.14rem]">
              New Clues from Red Blood Cells
            </div>
            <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
              A recent study in <span className="italic">Nature</span>, led by Australian scientist <span className="font-bold">Prof. Shaun Jackson</span>, shows that two types of blockages are at work after a stroke—not just one.
            </div>
            
            <div className="md:hidden mb-8">
            <div className="space-y-6">
                <div className="border border-[#C3EEFD] rounded-lg p-4">
                <h4 className="text-[#C3EEFD] font-bold text-[2rem] mb-3">Microclots</h4>
                <div className="space-y-2 text-[1.8rem]">
                    <div><span className="font-bold">Made of:</span> Platelets + fibrin (clotting proteins)</div>
                    <div><span className="font-bold">Where:</span> Small arteries and veins</div>
                    <div><span className="font-bold">Problem:</span> Can be broken down with drugs like t-PA, but not always fast enough</div>
                </div>
                </div>
                <div className="border border-[#C3EEFD] rounded-lg p-4">
                <h4 className="text-[#C3EEFD] font-bold text-[2rem] mb-3">RBC Aggregates</h4>
                <div className="space-y-2 text-[1.8rem]">
                    <div><span className="font-bold">Made of:</span> Damaged red blood cells that clump together</div>
                    <div><span className="font-bold">Where:</span> Tiny capillaries</div>
                    <div><span className="font-bold">Problem:</span> These form dense blockages that typical stroke drugs don't clear</div>
                </div>
                </div>
            </div>
            </div>

            <div className="hidden md:block overflow-x-auto mb-2">
              <table className="w-full border-collapse border border-[#C3EEFD] text-[2.1rem] leading-[3.1rem]">
                <thead>
                  <tr className="text-[#C3EEFD] bg-[#000]">
                    <th className="border border-[#C3EEFD] p-4 text-left font-bold">Type of Blockage</th>
                    <th className="border border-[#C3EEFD] p-4 text-left font-bold">What it's Made Of</th>
                    <th className="border border-[#C3EEFD] p-4 text-left font-bold">Where it Happens</th>
                    <th className="border border-[#C3EEFD] p-4 text-left font-bold">Why it's a Problem</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-[#C3EEFD] p-4 text-[#C3EEFD] font-bold">Microclots</td>
                    <td className="border border-[#C3EEFD] p-4">Platelets + fibrin (clotting proteins)</td>
                    <td className="border border-[#C3EEFD] p-4">Small arteries and veins</td>
                    <td className="border border-[#C3EEFD] p-4">Can be broken down with drugs like t-PA, but not always fast enough</td>
                  </tr>
                  <tr>
                    <td className="border border-[#C3EEFD] p-4 text-[#C3EEFD] font-bold">RBC Aggregates</td>
                    <td className="border border-[#C3EEFD] p-4">Damaged red blood cells that clump together</td>
                    <td className="border border-[#C3EEFD] p-4">Tiny capillaries</td>
                    <td className="border border-[#C3EEFD] p-4">These form dense blockages that typical stroke drugs don't clear</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
              In simple terms, platelets microclots, can worsen blood flow in downstream small blood vessels, triggering a previously unrecognised pathological process that causes red blood cell damage and capillary occlusion. It's a two-step traffic jam, deep inside the brain.
            </div>
            
            <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
              Prof. Jackson explains:
            </div>
            <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem] italic">
              "We've learned that opening the big arteries is only part of the story. If blood can't reach the smallest vessels, patients may still suffer major brain damage."
            </div>
          </div>

          <div className="flex flex-col gap-8 mb-[30px]">
            <div className="text-[#9EE7FF] font-syne text-[2.8rem] font-bold leading-[3.8rem] tracking-[.14rem]">
              Enter TBO-309: A New Type of Anti-Clotting Drug
            </div>
            <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
              This discovery also shines a light on TBO-309, a new drug in development by ThromBio. It's being designed to unblock large arteries in the brain but may also prevent the downstream formation of platelet clumps in the microcirculation (microclots)—without causing dangerous bleeding.
            </div>
            <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
              Unlike older anti-clotting drugs, TBO-309 primarily targets disease-causing clots, not the normal healthy clotting process, making it more precise and potentially safer.
            </div>
          </div>

          <div className="flex flex-col gap-8 mb-[30px]">
            <div className="text-[#9EE7FF] font-syne text-[2.8rem] font-bold leading-[3.8rem] tracking-[.14rem]">
              How It's Being Tested
            </div>
            <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
              TBO-309 is now being trialled in stroke patients through the STARS study, with a follow-up trial (Co-STARS) on the way. These studies are designed to find out:
            </div>
            <div className="flex flex-col gap-6 pl-8">
              <div className="flex gap-4">
                <div className="w-3 h-3 bg-white rounded-full mt-3 flex-shrink-0"></div>
                <div>Is TBO-309 safe for stroke patients?</div>
              </div>
              <div className="flex gap-4">
                <div className="w-3 h-3 bg-white rounded-full mt-3 flex-shrink-0"></div>
                <div>Does it help improve blood flow in the brain and reduce brain injury?</div>
              </div>
              <div className="flex gap-4">
                <div className="w-3 h-3 bg-white rounded-full mt-3 flex-shrink-0"></div>
                <div>Can it work alongside standard treatments like t-PA?</div>
              </div>
            </div>
            <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
              If it does all three, it could become a key part of the treatment puzzle.
            </div>
          </div>

          <div className="flex flex-col gap-8 mb-[30px]">
            <div className="text-[#9EE7FF] font-syne text-[2.8rem] font-bold leading-[3.8rem] tracking-[.14rem]">
              Why It Matters
            </div>
            <div className="flex flex-col gap-6 pl-8">
              <div className="flex gap-4">
                <div className="w-3 h-3 bg-white rounded-full mt-3 flex-shrink-0"></div>
                <div>Over 40 million people suffer from stroke or heart attack each year.</div>
              </div>
              <div className="flex gap-4">
                <div className="w-3 h-3 bg-white rounded-full mt-3 flex-shrink-0"></div>
                <div>Many patients don't recover fully—even after fast hospital treatment—because of poor microcirculation.</div>
              </div>
              <div className="flex gap-4">
                <div className="w-3 h-3 bg-white rounded-full mt-3 flex-shrink-0"></div>
                <div>Existing drugs like t-PA work on large clots, but not on platelet and red blood cell blockages.</div>
              </div>
              <div className="flex gap-4">
                <div className="w-3 h-3 bg-white rounded-full mt-3 flex-shrink-0"></div>
                <div>TBO-309 targets the problem at the source—platelet clumping that may trigger everything downstream.</div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-8 mb-[30px]">
            <div className="text-[#9EE7FF] font-syne text-[2.8rem] font-bold leading-[3.8rem] tracking-[.14rem]">
              What Makes TBO-309 Different?
            </div>
            
            <div className="grid md:grid-cols-2 gap-12">
              <div className="border-[5px] border-[#C3EEFD] rounded-[3rem] p-20">
                <div className="text-center text-[2.8rem] leading-[2.8rem] mb-6">
                  What It Does
                </div>
                <div className="flex flex-col gap-4">
                  <div className="flex gap-4">
                    <div className="w-3 h-3 bg-white rounded-full mt-3 flex-shrink-0"></div>
                    <div>Prevents pathological platelet clots, without inhibiting normal clotting</div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-3 h-3 bg-white rounded-full mt-3 flex-shrink-0"></div>
                    <div>Works well with t-PA</div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-3 h-3 bg-white rounded-full mt-3 flex-shrink-0"></div>
                    <div>May prevent red blood cell clumping</div>
                  </div>
                </div>
              </div>
              
              <div className="border-[5px] border-[#C3EEFD] rounded-[3rem] p-20">
                <div className="text-center text-[2.8rem] leading-[2.8rem] mb-6">
                  Why It Matters
                </div>
                <div className="flex flex-col gap-4">
                  <div className="flex gap-4">
                    <div className="w-3 h-3 bg-white rounded-full mt-3 flex-shrink-0"></div>
                    <div>Less chance of bleeding compared to older drugs</div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-3 h-3 bg-white rounded-full mt-3 flex-shrink-0"></div>
                    <div>Could enhance clot-busting and protect small vessels</div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-3 h-3 bg-white rounded-full mt-3 flex-shrink-0"></div>
                    <div>Stops the "second hit" in no-reflow before it starts</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-8 mb-[30px]">
            <div className="text-[#9EE7FF] font-syne text-[2.8rem] font-bold leading-[3.8rem] tracking-[.14rem]">
              The Bigger Picture
            </div>
            <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
              If these trials go well, TBO-309 could:
            </div>
            <div className="flex flex-col gap-6 pl-8">
              <div className="flex gap-4">
                <div className="w-3 h-3 bg-white rounded-full mt-3 flex-shrink-0"></div>
                <div>Help patients recover better after stroke or heart attack</div>
              </div>
              <div className="flex gap-4">
                <div className="w-3 h-3 bg-white rounded-full mt-3 flex-shrink-0"></div>
                <div>Offer a safer option for those who can't receive t-PA</div>
              </div>
              <div className="flex gap-4">
                <div className="w-3 h-3 bg-white rounded-full mt-3 flex-shrink-0"></div>
                <div>Open a whole new way to treat microvascular disease</div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-8 mb-[60px]">
            <div className="text-[#9EE7FF] font-syne text-[2.8rem] font-bold leading-[3.8rem] tracking-[.14rem]">
              In Summary
            </div>
            <div className="flex flex-col gap-6 pl-8">
              <div className="flex gap-4">
                <div className="w-3 h-3 bg-white rounded-full mt-3 flex-shrink-0"></div>
                <div>Stroke damage isn't just about big clots—it's also about what happens in the tiniest vessels.</div>
              </div>
              <div className="flex gap-4">
                <div className="w-3 h-3 bg-white rounded-full mt-3 flex-shrink-0"></div>
                <div>New research shows two types of blockages: platelet-driven microclots and red cell aggregates.</div>
              </div>
              <div className="flex gap-4">
                <div className="w-3 h-3 bg-white rounded-full mt-3 flex-shrink-0"></div>
                <div>TBO-309 is a targeted drug that may stop the first blockage and prevent the second from forming.</div>
              </div>
              <div className="flex gap-4">
                <div className="w-3 h-3 bg-white rounded-full mt-3 flex-shrink-0"></div>
                <div>The STARS and Co-STARS trials are testing whether this can change outcomes for patients—and for good.</div>
              </div>
            </div>
          </div>
        </div>
        <SeriesNavigation 
          items={media}
          currentSlug="abc"
          basePath="/media"
        />
      </div>
    </>
  );
}